import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import {
  createServiceRemainder,
  deleteServiceRemainderById,
  getServiceRemainders,
  updateServiceRemainderById,
} from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import { delBtn, eyeBtn } from "../../../util/CommonIconStyle";
import { patchMandateSession } from "../../../util/mandateSessionStorage";
import ReminderTemplateModal from "./ReminderTemplateModal";

const tableHeader = [
  "templateName",
  "linkedServices",
  "occurrenceLabel",
  "lastUpdated",
  "extraStatus",
];

const getRemainderId = (tmp) =>
  tmp?.serviceRemainderId ?? tmp?.templateId ?? tmp?.id ?? null;

const ReminderTemplate = () => {
  const dispatch = useDispatch();
  const serviceRemainders = useSelector(
    (state) => state?.clinic?.serviceRemainders,
  );
  const clinicServices = useSelector((state) => state?.clinic?.services);
  const [searchValue, setSearchValue] = useState("");
  const [createVsble, setCreateVsble] = useState(false);
  const [delModVsble, setDelModVsble] = useState(false);
  const [selectedTemp, setSelectedTemp] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const mandateServices = useMemo(() => {
    if (!mandateDataRaw) return [];
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return Array.isArray(reqData?.services) ? reqData.services : [];
    } catch {
      return [];
    }
  }, [mandateDataRaw]);

  const serviceList = useMemo(() => {
    const source =
      clinicServices?.data?.length > 0
        ? clinicServices.data
        : Array.isArray(clinicServices) && clinicServices.length > 0
          ? clinicServices
          : mandateServices;
    return source ?? [];
  }, [clinicServices, mandateServices]);

  const templates = useMemo(
    () => (Array.isArray(serviceRemainders) ? serviceRemainders : []),
    [serviceRemainders],
  );

  const filteredTemplates = useMemo(() => {
    const q = searchValue?.trim()?.toLowerCase();
    if (!q) return templates;
    return templates?.filter((tmp) =>
      (tmp?.templateName ?? tmp?.name ?? "")?.toLowerCase()?.includes(q),
    );
  }, [templates, searchValue]);

  useEffect(() => {
    onApiCall();
  }, []);

  const onApiCall = () => {
    dispatch(getServiceRemainders()).then((res) => {
      const payload = res?.payload;
      if (payload == null) return;
      patchMandateSession({
        serviceRemainders: Array.isArray(payload) ? payload : [],
      });
    });
  };

  const getLinkedServiceLabel = (pln) => {
    const linked = pln?.linkedServices ?? pln?.serviceIds ?? [];
    if (!linked?.length) return "-";
    return (
      linked
        ?.map((ser) => {
          const id =
            typeof ser === "object" && ser !== null
              ? (ser?.serviceId ?? ser?.value)
              : ser;
          if (id == null || id === "") return "";
          const match = serviceList?.find(
            (s) => String(s?.serviceId) === String(id),
          );
          return match?.serviceName ?? match?.name ?? String(id);
        })
        ?.filter(Boolean)
        ?.join(", ") || "-"
    );
  };

  const handleCreate = () => {
    setSelectedTemp(null);
    setCreateVsble(true);
  };

  const handleEdit = (det) => {
    setSelectedTemp(det);
    setCreateVsble(true);
  };

  const handleDelete = (det) => {
    setSelectedTemp(det);
    setDelModVsble(true);
  };

  const handleCloseDel = () => {
    setSelectedTemp(null);
    setDelModVsble(false);
  };

  const handleConfirmDel = () => {
    const serviceRemainderId = getRemainderId(selectedTemp);
    if (!serviceRemainderId || isSaving) return;
    setIsSaving(true);
    dispatch(deleteServiceRemainderById(serviceRemainderId)).then((res) => {
      setIsSaving(false);
      if (res?.payload) {
        onApiCall();
        handleCloseDel();
      }
    });
  };

  const handleCloseCreate = () => {
    setCreateVsble(false);
    setSelectedTemp(null);
  };

  const handleSave = (data) => {
    if (isSaving) return;
    const serviceRemainderId = getRemainderId(selectedTemp);
    setIsSaving(true);
    const req = serviceRemainderId
      ? dispatch(updateServiceRemainderById({ serviceRemainderId, data }))
      : dispatch(createServiceRemainder(data));
    req.then((res) => {
      setIsSaving(false);
      if (res?.payload) {
        onApiCall();
        handleCloseCreate();
      }
    });
  };

  const getTableList = () => {
    return (filteredTemplates ?? [])?.map((pln) => ({
      templateName: pln?.templateName ?? pln?.name ?? "-",
      linkedServices: getLinkedServiceLabel(pln),
      occurrenceLabel: pln?.occurrenceLabel || "Dose",
      lastUpdated: pln?.updatedDate
        ? moment(new Date(pln?.updatedDate)).format("DD MMM YYYY, HH:mm")
        : pln?.createdDate
          ? moment(new Date(pln?.createdDate)).format("DD MMM YYYY, HH:mm")
          : "-",
      extraStatus: (
        <div className="flex-row">
          <div className="cursor" onClick={() => handleDelete(pln)}>
            <div className="flex-center">
              <DeleteOutlineIcon sx={delBtn} />
            </div>
            <Typography className="red3 fs8 txt-semi-bold text-align-center">
              Delete
            </Typography>
          </div>
          <div className="cursor ml10" onClick={() => handleEdit(pln)}>
            <div className="flex-center">
              <EditIcon sx={eyeBtn} />
            </div>
            <Typography className="blue-color fs8 txt-semi-bold text-align-center">
              Edit
            </Typography>
          </div>
        </div>
      ),
    }));
  };

  return (
    <>
      <div>
        <div className="flex-row-ali-cen flex-center mt5">
          <div className="w30Per">
            <CustomTextField
              placeholder="Search"
              name="name"
              fullWidth
              handleChange={(e) => setSearchValue(e?.target?.value)}
              value={searchValue}
              backgroundColor={AppColors.lightBlue2}
            />
          </div>
          <div className="ml20">
            <CustomButton text="Create" onClick={handleCreate} smallBtn />
          </div>
        </div>

        <CustomTable
          columns={tableHeader}
          datas={getTableList()}
          grey
          maxHeight="70vh"
        />
      </div>

      <ReminderTemplateModal
        open={createVsble}
        onClose={handleCloseCreate}
        selectedTemp={selectedTemp}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <CustomModal
        open={delModVsble}
        onClose={handleCloseDel}
        header="Delete"
        modal
        modalWidth={30}
      >
        <div className="ml10">Are you sure want to delete the template?</div>
        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton
              text="Cancel"
              onClick={handleCloseDel}
              smallBtn
              disabled={isSaving}
            />
          </div>
          <div className="ml10">
            <CustomButton
              redBtn
              text="Confirm"
              onClick={handleConfirmDel}
              smallBtn
              disabled={isSaving}
            />
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default ReminderTemplate;
